import { supabase } from '../../lib/supabase';

/*
  THE ITEM MAPPING LIST, reading and writing, 5 October 2026.

  **What the list is.** Every ClaimSoft and Account Edge item number, each with
  the catalog product OnHand thinks it is and how it got there. Filled by
  `scripts/item-mapping/fill-the-list.sql` from the newest ClaimSoft and Account
  Edge lists: 34,857 suggestions on the day it was filled.

  **The owner's chain**, docs/items-rework/DECISIONS.md answers 29 to 31:
  ClaimSoft number, its "Vendor Part Number" (often the Account Edge number,
  which he typed there for an earlier project), the Account Edge item, its
  Vendor Item Number and vendor, the vendor's product. *"This can not be used as
  a 100% source of truth but it is a good starting point."* So every ClaimSoft
  suggestion waits for him, and only an exact Account Edge match was confirmed
  by itself.

  **A yes here is a yes on the list.** Nothing in this file writes to
  `org_items` or `org_item_numbers`; moving confirmed links into the real item
  record is a separate step, shown to him before it runs.

  **The list is 34,857 rows, so it is read a page at a time** and the counts are
  asked of the database rather than worked out from what is on screen.
*/

export const SYSTEMS = [
  { key: 'claimsoft',    label: 'ClaimSoft' },
  { key: 'account_edge', label: 'Account Edge' },
];

/*
  **The groups, by how a suggestion was found**, in the order he works them:
  the surest first. Each is a filter the database applies, so a count and the
  rows under it can never disagree.
*/
export const GROUPS = {
  claimsoft: [
    { key: 'agree',    label: 'Both routes agree',
      says: 'The Vendor Part Number column and the identical number both lead to the same Account Edge item.',
      where: q => q.eq('route', 'cs_both_routes_agree').is('warning', null) },
    { key: 'column',   label: 'Through the Vendor Part Number column',
      says: 'ClaimSoft’s Vendor Part Number holds an Account Edge item number. It often does, and not always.',
      where: q => q.eq('route', 'cs_vendor_part_to_ae').is('warning', null) },
    { key: 'same',     label: 'Same number in Account Edge',
      says: 'The ClaimSoft number is written exactly the same in Account Edge, and nothing else says so.',
      where: q => q.eq('route', 'cs_same_number_as_ae').is('warning', null) },
    { key: 'disagree', label: 'The two routes disagree',
      says: 'The column says one Account Edge item and the identical number says another. Both are shown; choosing one says no to the other.',
      where: q => q.eq('warning', 'routes_disagree') },
    { key: 'shared',   label: 'Shared with other numbers',
      says: 'Several ClaimSoft numbers lead to one Account Edge item, and they are not an ordinary number and its Medicare twin.',
      where: q => q.eq('warning', 'shared') },
    { key: 'not_found', label: 'Nothing found',
      says: 'No route reached Account Edge. Matching these by hand is not built yet.',
      where: q => q.eq('route', 'not_found') },
    { key: 'all',      label: 'Everything',
      says: 'Every ClaimSoft suggestion.',
      where: q => q },
  ],
  account_edge: [
    { key: 'exact',    label: 'Exact match',
      says: 'Account Edge’s own number is the vendor’s part number, same vendor. Confirmed by itself, on your word of 5 October.',
      where: q => q.eq('route', 'exact_vendor_part') },
    { key: 'vin',      label: 'Through the Vendor Item Number',
      says: 'Found through Account Edge’s Vendor Item Number field, which is not always right.',
      where: q => q.eq('route', 'ae_vendor_item_number').is('warning', null) },
    { key: 'disagree', label: 'Disagrees with an exact match',
      says: 'The Vendor Item Number points at a different product than the exact match does.',
      where: q => q.eq('warning', 'routes_disagree') },
    { key: 'not_found', label: 'Nothing found',
      says: 'Nothing matched. Most of these are under a vendor spelling Vendor Mapping has not pointed at a vendor yet.',
      where: q => q.eq('route', 'not_found') },
    { key: 'all',      label: 'Everything',
      says: 'Every Account Edge suggestion.',
      where: q => q },
  ],
};

export const STATES = {
  waiting:   { label: 'Waiting',   says: 'Nobody has said yes or no.' },
  confirmed: { label: 'Yes',       says: 'Confirmed. This is the product.' },
  rejected:  { label: 'No',        says: 'This is not the product.' },
  set_aside: { label: 'Not now',   says: 'Left for later, without an answer.' },
};

export const STATE_FILTERS = [
  { key: 'waiting',   label: 'Waiting on you' },
  { key: 'confirmed', label: 'Said yes' },
  { key: 'rejected',  label: 'Said no' },
  { key: 'set_aside', label: 'Not now' },
  { key: 'all',       label: 'Any answer' },
];

/* What each route is called beside a suggestion. */
export const ROUTE_WORDS = {
  exact_vendor_part:     'Exact match',
  ae_vendor_item_number: 'Vendor Item Number',
  cs_vendor_part_to_ae:  'Vendor Part Number column',
  cs_same_number_as_ae:  'Same number in Account Edge',
  cs_both_routes_agree:  'Both routes agree',
  not_found:             'Nothing found',
};

export const PAGE = 100;

const COLS = `
  id, system, number, number_key, description, via_account_edge, route,
  is_medicare, warning, state, decided_at, catalog_item_id,
  product:catalog_items(id, oh_number, vendor_part_number, vendor_description, our_description, vendor:vendors(name))
`;

export function groupOf(system, key) {
  return (GROUPS[system] || []).find(g => g.key === key) || GROUPS[system][0];
}

function scoped(q, { system, group, state, search }) {
  let out = groupOf(system, group).where(q.eq('system', system));
  if (state && state !== 'all') out = out.eq('state', state);
  const s = String(search || '').trim().toUpperCase().replace(/[%,()]/g, ' ');
  if (s) out = out.or('number_key.ilike.%' + s + '%,via_account_edge.ilike.%' + s + '%');
  return out;
}

/*
  **One page of the list.** Ordered by the number, then the row, so a page never
  shifts under him and the two rows of a disagreement sit together. It catches,
  because a read that throws leaves the screen saying "Loading" for ever.
*/
export async function loadPage(filters, from = 0) {
  try {
    const { data, error } = await scoped(supabase.from('item_match_suggestions').select(COLS), filters)
      .order('number_key', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);
    return { rows: data || [], error };
  } catch (e) {
    return { rows: [], error: { message: e && e.message ? e.message : String(e) } };
  }
}

/* How many are in each group for this system and answer. A count, not a read. */
export async function loadCounts({ system, state }) {
  const groups = GROUPS[system] || [];
  const results = await Promise.all(groups.map(g =>
    scoped(supabase.from('item_match_suggestions').select('id', { count: 'exact', head: true }),
      { system, group: g.key, state, search: '' })
      .then(r => [g.key, r])));
  const out = {};
  for (const [key, r] of results) {
    if (r.error) return { counts: null, error: r.error };
    out[key] = r.count || 0;
  }
  return { counts: out, error: null };
}

/*
  **Give one suggestion an answer, or take its answer away.**

  It only acts if the suggestion is still where the screen last saw it, so two
  tabs cannot both answer it. **And the row is read back**, because a save that
  no rule allows changes no rows, raises nothing and answers "fine", which is
  this project's oldest fault.

  **Choosing one side of a disagreement says no to the other**, because his rule
  is one number to one item: both cannot be right. Taking a yes away puts the
  other side back to waiting too, so an undo undoes the whole choice. (It puts
  it back to waiting, not to whatever it was before, because nothing records
  what that was.)
*/
export async function answer(row, state, userId) {
  const now = new Date().toISOString();
  const decided = state === 'waiting'
    ? { decided_by: null, decided_at: null }
    : { decided_by: userId || null, decided_at: now };

  const { data, error } = await supabase.from('item_match_suggestions')
    .update({ state, ...decided, updated_at: now })
    .eq('id', row.id)
    .eq('state', row.state)
    .select('id, state, decided_at');
  if (error) return { ok: false, error };
  if (!data || data.length !== 1 || data[0].state !== state) {
    return { ok: false, error: { message: 'Somebody else answered this one first, or it did not save. Nothing was changed.' } };
  }

  /*
    **The rival is any other suggestion for the same number**, not only one that
    carries the warning: on Account Edge the exact match is confirmed and has no
    warning, and the Vendor Item Number row that disagrees with it does. Only the
    81 ClaimSoft and 19 Account Edge numbers have a rival at all.
  */
  let others = [];
  const choosing = state === 'confirmed';
  const unchoosing = state === 'waiting' && row.state === 'confirmed';
  if (choosing || unchoosing) {
    let q = supabase.from('item_match_suggestions')
      .update(choosing
        ? { state: 'rejected', ...decided, updated_at: now }
        : { state: 'waiting', decided_by: null, decided_at: null, updated_at: now })
      .eq('system', row.system)
      .eq('number_key', row.number_key)
      .neq('id', row.id);
    q = choosing ? q.neq('state', 'rejected') : q.eq('state', 'rejected');
    const { data: changed, error: otherError } = await q.select('id, state');
    if (otherError) return { ok: true, error: null, row: data[0], others: [], otherError };
    others = changed || [];
  }
  return { ok: true, error: null, row: data[0], others };
}

/*
  **Say yes to a whole group at once**, for "Both routes agree", which is where
  he asked to be able to do that. Only the rows still waiting in it are changed,
  and the database's own count of what changed is compared with what he was
  told he was confirming.
*/
export async function confirmGroup({ system, group }, expected, userId) {
  const now = new Date().toISOString();
  const { data, error } = await scoped(
    supabase.from('item_match_suggestions')
      .update({ state: 'confirmed', decided_by: userId || null, decided_at: now, updated_at: now }),
    { system, group, state: 'waiting', search: '' })
    .select('id');
  if (error) return { ok: false, error, changed: 0 };
  const changed = (data || []).length;
  return { ok: changed === expected, error: null, changed };
}
